import React, { useEffect, useRef, forwardRef, RefObject } from 'react';
import Loader from '../Loader';

interface EndlessScrollProps {
  items: unknown[];
  loading: boolean;
  onLoadMore: () => void | Promise<void>;
  isStopLoad: boolean;
  forwardedRef: RefObject<HTMLDivElement>;
  rootMargin?: string;
}

const EndlessScroll: React.FC<
  EndlessScrollProps & { Component: React.ComponentType }
> = ({
  onLoadMore,
  loading,
  isStopLoad,
  forwardedRef,
  items,
  Component,
  rootMargin = '200% 0px',
  ...props
}) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const pendingRef = useRef(false);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || isStopLoad) return;

    const io = new IntersectionObserver(
      entries => {
        const visible = entries.some(e => e.isIntersecting);
        if (!visible) return;
        if (loading || isStopLoad || pendingRef.current) return;

        pendingRef.current = true;
        Promise.resolve(onLoadMore()).finally(() => {
          pendingRef.current = false;
        });
      },
      {
        root: null,
        rootMargin,
        threshold: 0.01
      }
    );

    io.observe(node);
    return () => io.disconnect();
  }, [onLoadMore, loading, isStopLoad, rootMargin]);

  return (
    <>
      <Component {...props} forwardedRef={forwardedRef} />

      <div ref={sentinelRef} aria-hidden style={{ height: 1 }} />

      <Loader
        loading={loading && !!items?.length && !isStopLoad}
        optionalClass="endless-loader"
      />
    </>
  );
};

export const withEndlessScroll = <P extends object>(
  Component: React.ComponentType<
    P & { forwardedRef: RefObject<HTMLDivElement> }
  >
) => {
  const ForwardedComponent = forwardRef<
    HTMLDivElement,
    P & Omit<EndlessScrollProps, 'forwardedRef'>
  >((props, ref) => (
    <EndlessScroll {...props} forwardedRef={ref} Component={Component} />
  ));

  const name = Component.displayName || Component.name;
  ForwardedComponent.displayName = `withEndlessScroll(${name})`;

  return ForwardedComponent;
};
